/**
 * HW video: prepare a native <video> the moment a viewer presses play.
 *
 * Two things happen on that click:
 *
 * 1. Audio is turned on. hw-video renders its <video> muted so the poster
 *    preview and any autoplay can start without tripping browser autoplay
 *    policy. Pressing the play control is both an explicit request to watch
 *    and the user gesture browsers require, so sound is enabled at that
 *    point. This must stay synchronous inside the click: WebKit pauses a
 *    video that becomes un-muted without a user gesture. It works because
 *    deferred-media's delegated handler runs in the capture phase (see
 *    registerEventListeners in assets/component.js), so the <video> is
 *    already in the DOM by the time this bubble-phase listener fires.
 *
 * 2. The poster attribute is dropped once frames are available, to work
 *    around Safari showing a frozen still while audio plays. Every media
 *    element has a spec "show poster flag", and WebKit paints the poster
 *    image instead of video frames while that flag is set and a poster URL
 *    is present. deferred-media appends the cloned <video> and calls play()
 *    in the same task (loadContent in assets/media.js). Inserting a media
 *    element *queues* the resource selection algorithm; play() runs it early
 *    and clears the flag, then the queued run sets it back to true. Nothing
 *    clears it again, because play() only clears the flag while `paused` is
 *    true — which is why pausing and playing again is the one interaction
 *    that recovers the picture. Removing the poster URL leaves WebKit with
 *    nothing to paint but the video itself. Chromium drops the poster as
 *    soon as it has a decoded frame, so this is a no-op there.
 *
 * Card background videos are decorative cover layers and stay silent.
 */

(function () {
  'use strict';

  const PLAY_BUTTON = '.deferred-media__poster-button';
  const VIDEO_ROOT = '.hw-video-deferred:not(.hw-card__bg-video)';
  const MAX_FRAMES = 10;
  const HAVE_CURRENT_DATA = 2;

  /**
   * @param {HTMLVideoElement} video
   */
  function unmute(video) {
    video.muted = false;
    video.removeAttribute('muted');

    if (video.volume === 0) {
      video.volume = 1;
    }
  }

  /**
   * Keeps the poster as the loading placeholder, then hands over to the video
   * once there is a frame to show.
   *
   * @param {HTMLVideoElement} video
   */
  function dropPoster(video) {
    if (!video.hasAttribute('poster')) {
      return;
    }

    if (video.readyState >= HAVE_CURRENT_DATA) {
      video.removeAttribute('poster');
      return;
    }

    video.addEventListener('loadeddata', () => video.removeAttribute('poster'), { once: true });
  }

  /**
   * deferred-media clones the <video> out of a <template> in response to the
   * same click. It normally lands in the DOM before this runs, but fall back
   * to polling frames in case that ordering ever changes — note that unmute()
   * loses its user gesture on that path, so it is a last resort, not the
   * expected route.
   *
   * @param {HTMLElement} root
   * @param {number} [frame]
   */
  function prepareWhenReady(root, frame) {
    const attempt = frame || 0;
    const video = root.querySelector('video');

    if (video instanceof HTMLVideoElement) {
      unmute(video);
      dropPoster(video);
      return;
    }

    if (attempt < MAX_FRAMES) {
      requestAnimationFrame(() => prepareWhenReady(root, attempt + 1));
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const button = target.closest(PLAY_BUTTON);
    if (!button) {
      return;
    }

    const root = button.closest(VIDEO_ROOT);
    if (!(root instanceof HTMLElement)) {
      return;
    }

    prepareWhenReady(root);
  });
})();
